import React from "react";
import PingStatusTooltip from "../../../../Components/PingStatus/PingStatusTooltip";
import { Box, IconButton, Typography } from "@mui/material";
import { Tooltip } from "antd";
import { getCurrentTimestamp } from "../../../../Helper/DatePicker/DateConverter";
import CustomIcon from "../../../../Components/CustomIcon";
import copy from "copy-to-clipboard";
import { useToast } from "../../../../Hooks/useToast";
import { getSohColor, formatDaysAge, getEmptyCellText } from "../SOHBreakdown/SOHBreakdownTableConfig";
import { timeAgo } from "../SOHDetails/SOHDetailsTableConfig";

// ─── ID cell (battery ID or device ID) ────────────────────────────────────────
const IDCell = ({ text, isActive, onNavigate }) => {
  const { openNotification } = useToast();

  const handleCopy = (e) => {
    e.stopPropagation();
    copy(text);
    openNotification({ text: "Copied to Clipboard" });
  };

  return (
    <Box display="flex" alignItems="center" gap={0.75}>
      <PingStatusTooltip online={isActive}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            flexShrink: 0,
            backgroundColor: isActive ? "#2DB55D" : "#9E9E9E",
          }}
        />
      </PingStatusTooltip>
      <Typography
        variant="fw400s14lh24c0061CA"
        onClick={onNavigate}
        sx={{
          cursor: "pointer",
          "&:hover": { color: "#4D44D8 !important" },
        }}
      >
        {text}
      </Typography>
      <Tooltip title="Click to copy" placement="top">
        <IconButton
          disableRipple
          disableTouchRipple
          disableFocusRipple
          onClick={handleCopy}
        >
          <CustomIcon name="copy-icon" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// ─── Custom renderers factory (accepts navigate callback) ─────────────────────
export const getRulCustomRenderers = (onBatteryIDClick) => ({
  "days-to-duration": (record, column) => {
    const days = record?.[column.dataIndex];
    return (
      <Typography sx={{ fontSize: 13, color: "#202124" }}>
        {days == null || isNaN(days) ? getEmptyCellText(column, formatDaysAge(days)) : formatDaysAge(days)}
      </Typography>
    );
  },

  "digital-twin-link": (record) => (
    <IDCell
      text={record.deviceID}
      isActive={record.pingStatus?.toLowerCase() === "active"}
      onNavigate={() => onBatteryIDClick(record)}
    />
  ),
  // Legacy batteryID column type: shows the org-facing device id, never the internal
  // batteryID (navigation still uses it)
  "battery-id-link": (record) => (
    <IDCell
      text={record.deviceID || "-"}
      isActive={record.pingStatus?.toLowerCase() === "active"}
      onNavigate={() => onBatteryIDClick(record)}
    />
  ),
  "soh-progress-bar": (record, config) => {
    const soh = record?.sohEst;
    if (soh == null) return <Typography sx={{ fontSize: 13, color: "#9E9E9E" }}>{getEmptyCellText(config, "N/A")}</Typography>;
    const decimalPlaces = config?.decimalPlaces ?? 1;
    const color = getSohColor(soh);
    const width = Math.min(100, Math.max(0, soh));
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ width: 56, height: 5, bgcolor: "#E0E0E0", borderRadius: "3px", flexShrink: 0 }}>
          <Box sx={{ width: `${width}%`, height: "100%", bgcolor: color, borderRadius: "3px" }} />
        </Box>
        <Typography sx={{ fontSize: 13, color: "#202124" }}>{soh.toFixed(decimalPlaces)}%</Typography>
      </Box>
    );
  },
  "time-ago": (record) => (
    <Typography variant="fw400s14lh24c3D4B5A">{timeAgo(record.addedOn)}</Typography>
  ),
});

export const getRulCSVFileName = () =>
  `RUL_BatteryList_${getCurrentTimestamp("YYYY-MM-DD")}.csv`;
